import { cn } from "@/libs/cn";
import { CaretLeftIcon, CaretRightIcon } from "@/modules/common/assets/icons";
import { PaginationMetadata } from "@/modules/common/types/Pagination";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface PaginationCompactProps {
  metadata: PaginationMetadata;
  onChangePage: (nextPage: number) => void;
  onChangePageSize?: (nextSize: number) => void;
  minPageSize?: number;
  maxPageSize?: number;
  className?: string;
}

type PageItem = number | "ellipsis";

const PAGE_SIZE_STEP = 5;

function getPageNumbers(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 3) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, 2, currentPage, totalPages]);
  const sorted = Array.from(pages).sort((a, b) => a - b);

  const result: PageItem[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push("ellipsis");
    }
    result.push(sorted[i]);
  }

  return result;
}

const PaginationCompact = ({
  metadata,
  onChangePage,
  onChangePageSize,
  minPageSize = 5,
  maxPageSize = 500,
  className,
}: PaginationCompactProps) => {
  const { totalItems, currentPage, totalPages, limit } = metadata;
  const [pageSizeInput, setPageSizeInput] = useState(String(limit));

  useEffect(() => {
    setPageSizeInput(String(limit));
  }, [limit]);

  const pageItems = useMemo(() => getPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      if (page !== currentPage && page >= 1 && page <= totalPages) onChangePage(page);
    },
    [currentPage, totalPages, onChangePage],
  );

  const decrementPageSize = useCallback(() => {
    if (!onChangePageSize) return;
    const next = Math.max(minPageSize, limit - PAGE_SIZE_STEP);
    if (next !== limit) onChangePageSize(next);
  }, [onChangePageSize, limit, minPageSize]);

  const incrementPageSize = useCallback(() => {
    if (!onChangePageSize) return;
    const next = Math.min(maxPageSize, limit + PAGE_SIZE_STEP);
    if (next !== limit) onChangePageSize(next);
  }, [onChangePageSize, limit, maxPageSize]);

  return (
    <div className={cn("flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between", className)}>
      {/* Left: page size control + results count */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-[#A0ACBC]">Mostrando</span>

        <div className="flex items-center border border-ui-border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={decrementPageSize}
            disabled={!onChangePageSize || limit <= minPageSize}
            className="px-2 py-1.5 hover:bg-ui-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <CaretLeftIcon className="h-3.5 w-3.5 text-ui-primary" />
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={pageSizeInput}
            disabled={!onChangePageSize}
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, "");
              const num = Number(raw);
              if (raw === "" || num <= maxPageSize) setPageSizeInput(raw);
            }}
            onBlur={() => {
              const parsed = Math.min(maxPageSize, Math.max(minPageSize, Number(pageSizeInput) || limit));
              setPageSizeInput(String(parsed));
              if (parsed !== limit) onChangePageSize?.(parsed);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            className="max-w-7 text-sm font-medium text-[#A0ACBC] text-center bg-transparent outline-none disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={incrementPageSize}
            disabled={!onChangePageSize || limit >= maxPageSize}
            className="px-2 py-1.5 hover:bg-ui-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <CaretRightIcon className="h-3.5 w-3.5 text-ui-primary" />
          </button>
        </div>

        <span className="text-sm text-[#A0ACBC] whitespace-nowrap">de {totalItems} resultados</span>
      </div>

      {/* Right: page navigation */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage <= 1}
          className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm border border-ui-border rounded-lg text-[#A0ACBC] hover:bg-ui-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <CaretLeftIcon className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Anterior</span>
        </button>

        <span className="sm:hidden text-sm text-[#A0ACBC] px-1 select-none">
          <span className="font-medium text-[#A0ACBC]">{currentPage}</span> / {totalPages}
        </span>

        <div className="hidden sm:flex items-center gap-1.5">
          {pageItems.map((item, index) =>
            item === "ellipsis" ? (
              <span key={`ellipsis-${index}`} className="px-1 text-sm text-[#A0ACBC] select-none">
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => goToPage(item)}
                disabled={item === currentPage}
                className={cn(
                  "min-w-8 h-8 px-2 text-sm rounded-lg border transition-colors",
                  item === currentPage
                    ? "bg-ui-primary text-white border-ui-primary cursor-default"
                    : "bg-white text-[#A0ACBC] border-ui-border hover:bg-ui-hover cursor-pointer",
                )}
              >
                {item}
              </button>
            ),
          )}
        </div>

        <button
          type="button"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-sm border border-ui-border rounded-lg text-[#101A25] hover:bg-ui-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <span className="hidden sm:inline">Próxima</span>
          <CaretRightIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default PaginationCompact;
